import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type { WorkerProfile, Profession, PaginatedResponse } from '@/types/worker';

export const ADMIN_KEYS = {
    all: ['admin'] as const,
    workers: (params?: Record<string, string>) => [...ADMIN_KEYS.all, 'workers', params] as const,
    professions: () => [...ADMIN_KEYS.all, 'professions'] as const,
};

export const useAdminWorkers = (params?: { status?: string; limit?: number; offset?: number }) => {
    return useQuery({
        queryKey: ADMIN_KEYS.workers(params as Record<string, string>),
        queryFn: async (): Promise<PaginatedResponse<WorkerProfile>> => {
            const searchParams = new URLSearchParams();
            if (params?.status) searchParams.set('status', params.status);
            if (params?.limit) searchParams.set('limit', String(params.limit));
            if (params?.offset) searchParams.set('offset', String(params.offset));
            const query = searchParams.toString();
            const { data } = await api.get(`${API_ROUTES.adminWorkers}${query ? `?${query}` : ''}`);
            return data;
        },
    });
};

export const useAdminProfessions = () => {
    return useQuery({
        queryKey: ADMIN_KEYS.professions(),
        queryFn: async (): Promise<Profession[]> => {
            const { data } = await api.get(API_ROUTES.adminProfessions);
            return data;
        },
        staleTime: 10 * 60 * 1000,
    });
};

export const useApproveWorker = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (uuid: string) => {
            const { data } = await api.post(API_ROUTES.adminApproveWorker(uuid));
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ADMIN_KEYS.all });
        },
    });
};

export const useSuspendWorker = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (uuid: string) => {
            const { data } = await api.post(API_ROUTES.adminSuspendWorker(uuid));
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ADMIN_KEYS.all });
        },
    });
};

export const useSetProfessions = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ uuid, professionUuids }: { uuid: string; professionUuids: string[] }) => {
            const { data } = await api.post(API_ROUTES.adminSetProfessions(uuid), {
                profession_uuids: professionUuids,
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ADMIN_KEYS.all });
        },
    });
};
